import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Message } from '../../types/messages';
import { MessageSquare } from 'lucide-react';
import { format } from 'date-fns';

export default function MessagesWidget() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'messages'),
        orderBy('createdAt', 'desc'),
        limit(3)
      ),
      (snapshot) => {
        const messageData: Message[] = [];
        snapshot.forEach((doc) => {
          const message = { id: doc.id, ...doc.data() } as Message;
          // Only show messages that are either sent to or from the current user
          if (message.senderId === user.uid || message.receiverId === user.uid) {
            messageData.push(message);
          }
        });
        setMessages(messageData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading messages:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Messages</h2>
          <MessageSquare className="w-5 h-5 text-text-secondary" />
        </div>

        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-start gap-4 p-4 rounded-lg bg-background"
            >
              <div className="w-8 h-8 bg-text-muted rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-1/4 mb-2" />
                <div className="h-3 bg-text-muted rounded w-3/4" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Messages</h2>
        <MessageSquare className="w-5 h-5 text-text-secondary" />
      </div>

      <div className="space-y-4">
        {messages.map((message) => (
          <Link
            key={message.id}
            to="/messages"
            className="flex items-start gap-4 p-4 rounded-lg bg-background hover:bg-primary-50 
                     transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-700">
                {message.senderId.substring(0, 2).toUpperCase()}
              </span>
            </div>

            <div className="flex-1">
              <h3 className="text-sm font-medium text-text-primary">
                {message.senderId === auth.currentUser?.uid ? 'You' : 'User'}
              </h3>
              <p className="text-sm text-text-secondary">{message.content}</p>
              <span className="text-xs text-text-muted">
                {(() => {
                  if (!message.createdAt) return 'Just now';
                  try {
                    const date = new Date(message.createdAt);
                    if (isNaN(date.getTime())) return 'Just now';
                    return format(date, 'p');
                  } catch {
                    return 'Just now';
                  }
                })()}
              </span>
            </div>
          </Link>
        ))}

        {messages.length === 0 && (
          <div className="text-center py-4">
            <p className="text-text-secondary">No recent messages</p>
          </div>
        )}
      </div>
    </div>
  );
}